import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useState } from "react";
import { Loading } from "app/lib/components/Loading";
import classNames from "classnames";
import { clientConfigFn } from "clientConfig";
import { groupBy } from "core/util/array";
import dayjs from "dayjs";
import { usePagedTransactions } from "../PaginationUtilities";
import MobileTransaction from "./MobileTransaction";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { CError } from "app/common/CError";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { Box } from "@mui/material";
import { useSafeArea } from "app/layout/Navbar/useSafeArea";
const TransactionDateCard = (props) => {
    const { BETA_showDetailView } = clientConfigFn();
    const handleClick = (transaction) => {
        if (BETA_showDetailView) {
            props.showDetailView(transaction);
        }
    };
    const groupItemClasses = classNames([
        "list-group-item",
        { "cursor-pointer": BETA_showDetailView },
    ]);
    return (_jsxs("div", { className: "card", children: [_jsx("div", { className: "card-header", children: dayjs(props.date).format("MMMM DD, YYYY") }), _jsx("ul", { className: "list-group list-group-flush", children: props.transactions.map((x) => (_jsxs("li", { className: groupItemClasses, onClick: () => handleClick(x), children: [_jsxs("div", { className: "left-item", children: [_jsx("p", { children: x.TransactionType }), _jsx("p", { className: "text-muted small", children: x.Note })] }), _jsx("div", { className: "right-item", children: _jsx(MobileTransaction, { transaction: x }) })] }, x.Id))) })] }));
};
const MobileTransactionList = ({ account, setDetailView, }) => {
    const { transactions, loading: loadingTransactions, error: errorTransactions, } = usePagedTransactions(account, true);
    const [isVisible, setIsVisible] = useState(false);
    const { loading, items, hasNextPage, error, loadMore, resetItems } = useLoadItems();
    const { transactionPageSize } = clientConfigFn();
    const [awaitingTransactionReset, setAwaitingTransactionReset] = useState();
    const { insetBottom } = useSafeArea();
    const groupedTransactions = transactions === undefined
        ? []
        : groupBy(transactions, (transaction) => dayjs(new Date(transaction.EffectiveDate))
            .endOf("day")
            .toDate(), (firstElement, secondElement) => secondElement.valueOf() - firstElement.valueOf());
    const groupedTransactionList = groupedTransactions.map((x) => (_jsx(TransactionDateCard, { date: x.key, transactions: x.elements, showDetailView: setDetailView }, x.key.toUTCString())));
    useEffect(() => {
        setAwaitingTransactionReset(transactions?.reduce((state, transaction) => {
            return state + transaction.Id;
        }, ""));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [account]);
    useEffect(() => {
        if (!loading) {
            const transactionIds = transactions?.reduce((state, transaction) => {
                return state + transaction.Id;
            }, "");
            if (transactionIds !== awaitingTransactionReset) {
                setAwaitingTransactionReset(transactionIds);
                resetItems();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transactions]);
    const ARRAY_SIZE = transactionPageSize || 20;
    const RESPONSE_TIME_IN_MS = 1200;
    function loadItems(startCursor = 0) {
        return new Promise((resolve) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let newArray = [];
            setTimeout(() => {
                if (groupedTransactionList.length >= items.length) {
                    for (let i = startCursor; i < startCursor + ARRAY_SIZE; i++) {
                        const transactionItem = groupedTransactionList[i];
                        const newItem = {
                            key: i,
                            value: transactionItem,
                        };
                        newArray = [...newArray, newItem];
                    }
                    resolve({ hasNextPage: true, data: newArray });
                }
                else {
                    resolve({ hasNextPage: false, data: newArray });
                }
            }, RESPONSE_TIME_IN_MS);
        });
    }
    function useLoadItems() {
        const [loading, setLoading] = React.useState(false);
        const [items, setItems] = React.useState([]);
        const [hasNextPage, setHasNextPage] = React.useState(true);
        const [error, setError] = React.useState();
        async function loadMore() {
            setLoading(true);
            try {
                const { data, hasNextPage: newHasNextPage } = await loadItems(items.length);
                setItems((current) => [...current, ...data]);
                setHasNextPage(newHasNextPage);
            }
            catch (err) {
                setError(err);
            }
            finally {
                setLoading(false);
            }
        }
        function resetItems() {
            setHasNextPage(true);
            setItems([]);
            setLoading(false);
        }
        return { loading, items, hasNextPage, error, loadMore, resetItems };
    }
    const [sentryRef] = useInfiniteScroll({
        loading,
        hasNextPage,
        onLoadMore: loadMore,
        // When there is an error, we stop infinite loading.
        // It can be reactivated by setting "error" state as undefined.
        disabled: !!error,
        // `rootMargin` is passed to `IntersectionObserver`.
        // We can use it to trigger 'onLoadMore' when the sentry comes near to become
        // visible, instead of becoming fully visible on the screen.
        rootMargin: "0px 0px 400px 0px",
    });
    const toggleVisibility = () => {
        if (window.scrollY > 300) {
            setIsVisible(true);
        }
        else {
            setIsVisible(false);
        }
    };
    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };
    useEffect(() => {
        window.addEventListener("scroll", toggleVisibility);
    }, []);
    if (loadingTransactions || transactions == null) {
        return _jsx(Loading, {});
    }
    if (errorTransactions) {
        return _jsx(CError, { error: "Could not retrieve account transactions." });
    }
    return (_jsxs("div", { className: "mobile-transaction-list-container", children: [items.map((item, index) => (_jsx("div", { children: item.value }, index))), (loading || hasNextPage) && (_jsx("p", { className: "dataEnd", ref: sentryRef, children: _jsx(Loading, {}) })), !hasNextPage && (_jsx("p", { className: "dataEnd", ref: sentryRef, children: "There are no more items to display." })), _jsx("div", { className: "scroll-to-top", children: isVisible && (_jsxs(_Fragment, { children: [_jsx("div", { className: "btn-scroll", onClick: scrollToTop, children: _jsx(ArrowUpwardIcon, {}) }), _jsx(Box, { height: `${insetBottom}px` })] })) })] }));
};
export default MobileTransactionList;
